import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { transactionApi, accountApi, documentApi, clientApi } from '@/services/api'
import { useClientStore } from '@/stores/clientStore'
import type { Transaction, Account, Document } from '@/types'
import { formatCurrency } from '@/utils/currency'
import { Plus, Receipt, Search, ChevronLeft, ChevronRight, Paperclip, FileText, Image, File } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button, buttonVariants } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'

const SOURCE_BADGE: Record<string, 'default' | 'info' | 'warning' | 'destructive' | 'success'> = {
  manual: 'default',
  bank_import: 'info',
  journal: 'default',
  depreciation: 'warning',
}

const SOURCE_LABEL: Record<string, string> = {
  manual: 'Manual',
  bank_import: 'Bank Import',
  journal: 'Journal',
  depreciation: 'Depreciation',
}

const PAGE_SIZE = 50

function DocIcon({ mimeType }: { mimeType: string }) {
  if (mimeType.startsWith('image/')) return <Image className="w-3 h-3 text-blue-400" />
  if (mimeType === 'application/pdf') return <FileText className="w-3 h-3 text-red-400" />
  return <File className="w-3 h-3 text-[#CCCED2]" />
}

function formatSize(bytes: number | null) {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function Transactions() {
  const { clientId } = useParams<{ clientId: string }>()
  const setCurrentClient = useClientStore((s) => s.setCurrentClient)
  const [currency, setCurrency] = useState('USD')
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [docs, setDocs] = useState<Document[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [accounts, setAccounts] = useState<Account[]>([])
  const [source, setSource] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [accountId, setAccountId] = useState('')
  const [page, setPage] = useState(1)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  useEffect(() => {
    if (!clientId) return
    clientApi.get(clientId).then((res) => { setCurrentClient(res.data.client); setCurrency(res.data.client.currency) })
    accountApi.list(clientId).then((res) => setAccounts(res.data.accounts))
    documentApi.list(clientId).then((res) => setDocs(res.data.documents)).catch(() => {})
    loadTransactions()
  }, [clientId])

  function docsForTxn(txnId: string) {
    return docs.filter((d) => d.linked_transaction_id === txnId)
  }

  async function loadTransactions(filters?: { source?: string; startDate?: string; endDate?: string }) {
    setLoading(true)
    try {
      const params: Record<string, string> = {}
      const s = filters?.source ?? source
      const sd = filters?.startDate ?? startDate
      const ed = filters?.endDate ?? endDate
      if (s) params.source = s
      if (sd) params.start_date = sd
      if (ed) params.end_date = ed
      const res = await transactionApi.list(clientId!, params)
      setTransactions(res.data.transactions)
      setTotal(res.data.total)
      setPage(1)
    } finally {
      setLoading(false)
    }
  }

  function handleApplyFilters(e: React.FormEvent) {
    e.preventDefault()
    loadTransactions({ source, startDate, endDate })
  }

  function handleClearFilters() {
    setSource(''); setStartDate(''); setEndDate(''); setAccountId('')
    loadTransactions({ source: '', startDate: '', endDate: '' })
  }

  const filtered = accountId
    ? transactions.filter((txn) => txn.lines.some((l) => l.account_id === accountId))
    : transactions
  const pageCount = Math.ceil(filtered.length / PAGE_SIZE)
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  const hasActiveFilters = source || startDate || endDate || accountId

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Transactions</h1>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">
            {filtered.length !== total ? `${filtered.length} of ${total}` : total}{' '}
            {total === 1 ? 'transaction' : 'transactions'}
          </p>
        </div>
        <Link to={`/clients/${clientId}/journals/new`} className={cn(buttonVariants({ variant: 'create' }), 'gap-2')}>
          <Plus className="w-4 h-4" /> New Journal
        </Link>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleApplyFilters} className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-36 space-y-1.5">
              <Label>Source</Label>
              <select value={source} onChange={(e) => setSource(e.target.value)} className="input">
                <option value="">All sources</option>
                <option value="manual">Manual</option>
                <option value="bank_import">Bank Import</option>
                <option value="journal">Journal</option>
                <option value="depreciation">Depreciation</option>
              </select>
            </div>
            <div className="flex-1 min-w-36 space-y-1.5">
              <Label>From</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="flex-1 min-w-36 space-y-1.5">
              <Label>To</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <div className="flex-1 min-w-40 space-y-1.5">
              <Label>Account</Label>
              <select value={accountId} onChange={(e) => { setAccountId(e.target.value); setPage(1) }} className="input">
                <option value="">All accounts</option>
                {accounts.filter((a) => !a.is_archived).map((a) => (
                  <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2 pb-0.5">
              <Button type="submit" size="sm">
                <Search className="w-3.5 h-3.5" /> Filter
              </Button>
              {hasActiveFilters && (
                <Button type="button" variant="outline" size="sm" onClick={handleClearFilters}>Clear</Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {loading ? (
        <Card>
          <CardContent className="p-5 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
          </CardContent>
        </Card>
      ) : paginated.length > 0 ? (
        <>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-[13px]">Date</TableHead>
                    <TableHead className="text-[13px]">Description</TableHead>
                    <TableHead className="text-[13px]">Reference</TableHead>
                    <TableHead className="text-[13px]">Source</TableHead>
                    <TableHead className="text-right text-[13px]">Debit</TableHead>
                    <TableHead className="text-right text-[13px]">Credit</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginated.map((txn) => {
                    const totalDebit = txn.lines.reduce((s, l) => s + parseFloat(l.debit), 0)
                    const totalCredit = txn.lines.reduce((s, l) => s + parseFloat(l.credit), 0)
                    const src = txn.source
                    const linkedDocs = docsForTxn(txn.id)
                    return (
                      <React.Fragment key={txn.id}>
                        <TableRow>
                          <TableCell className="whitespace-nowrap text-[15px] text-[rgba(0,10,30,0.75)]">{txn.date}</TableCell>
                          <TableCell className="text-[15px] font-bold text-[#000A1E]">
                            {txn.description || <span className="text-[#CCCED2]">&mdash;</span>}
                          </TableCell>
                          <TableCell className="text-[rgba(0,10,30,0.75)] font-mono text-[13px]">
                            {txn.reference || <span className="text-[#CCCED2]">&mdash;</span>}
                          </TableCell>
                          <TableCell>
                            <Badge variant={SOURCE_BADGE[src] ?? 'default'}>
                              {SOURCE_LABEL[src] ?? src.replace(/_/g, ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-[15px] text-[#000A1E]">
                            {totalDebit > 0 ? formatCurrency(totalDebit, currency) : <span className="text-[#CCCED2]">&mdash;</span>}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-[15px] text-[#000A1E]">
                            {totalCredit > 0 ? formatCurrency(totalCredit, currency) : <span className="text-[#CCCED2]">&mdash;</span>}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => setExpandedId(expandedId === txn.id ? null : txn.id)}
                              title="Attachments"
                            >
                              <Paperclip className={cn(
                                'w-3.5 h-3.5',
                                linkedDocs.length > 0 ? 'text-[#0078C8]' : 'text-[#CCCED2]',
                              )} />
                            </Button>
                          </TableCell>
                        </TableRow>
                        {expandedId === txn.id && (
                          <TableRow className="bg-muted/30 hover:bg-muted/30">
                            <TableCell colSpan={7} className="px-6 py-3">
                              {linkedDocs.length === 0 ? (
                                <p className="text-[13px] text-[rgba(0,10,30,0.75)]">No documents attached to this transaction.</p>
                              ) : (
                                <div className="flex flex-wrap gap-3">
                                  {linkedDocs.map((doc) => (
                                    <a
                                      key={doc.id}
                                      href={doc.file_path}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="flex items-center gap-1.5 text-[13px] text-[#000A1E] hover:text-[#0078C8] bg-white border border-border rounded-md px-2.5 py-1.5 transition-colors"
                                    >
                                      <DocIcon mimeType={doc.mime_type} />
                                      <span className="font-bold truncate max-w-[160px]">{doc.file_name}</span>
                                      {doc.category && (
                                        <Badge variant="default" className="capitalize text-[10px] px-1 py-0">
                                          {doc.category}
                                        </Badge>
                                      )}
                                      <span className="text-[rgba(0,10,30,0.75)]">{formatSize(doc.file_size)}</span>
                                    </a>
                                  ))}
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    )
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {pageCount > 1 && (
            <div className="flex items-center justify-between px-1">
              <p className="text-[13px] text-[rgba(0,10,30,0.75)]">
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
              </p>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon-sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-[13px] text-[#000A1E] px-2">{page} / {pageCount}</span>
                <Button variant="ghost" size="icon-sm" onClick={() => setPage((p) => Math.min(pageCount, p + 1))} disabled={page === pageCount}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="p-16 text-center">
            <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Receipt className="w-7 h-7 text-[rgba(0,10,30,0.75)]" />
            </div>
            {hasActiveFilters ? (
              <>
                <h3 className="text-[17px] font-bold text-[#000A1E] leading-[24px] mb-1">No matching transactions</h3>
                <p className="text-[15px] text-[rgba(0,10,30,0.75)] mb-4">Try adjusting the filters above.</p>
                <Button variant="outline" onClick={handleClearFilters}>Clear filters</Button>
              </>
            ) : (
              <>
                <h3 className="text-[17px] font-bold text-[#000A1E] leading-[24px] mb-1">No transactions yet</h3>
                <p className="text-[15px] text-[rgba(0,10,30,0.75)] max-w-sm mx-auto mb-6">
                  Transactions appear here once you create journal entries or import bank statements.
                </p>
                <Link to={`/clients/${clientId}/journals/new`} className={cn(buttonVariants({ variant: 'create' }), 'gap-2')}>
                  <Plus className="w-4 h-4" /> Create First Journal
                </Link>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
